/**
 * DM-7283: decision logic for the Moderation page's Reports tab.
 *
 * A report row's target is a post, a comment, or a user. The first two go
 * through the item action; a report against a user has its own resolution
 * route. Split out for the reason userDeletion.logic.ts is: apps/admin-ui is
 * never ESLint'd or complexity-checked in CI and its jest suite matches `.ts`
 * only, so pure logic in a sibling file is the one part of a page that can be
 * tested.
 */

import type {
  FlaggedContentItem,
  ModerationReportItem,
  OrganizationImageModerationWire,
  ProfileFieldsWire,
  UserReportResolutionAction,
  UserReportsResolved,
} from '../api/adminOps';
import type { ReasonOption } from '../components/ui';

/** content_reports.target_type value for a report against a user. */
export const USER_REPORT_TARGET_TYPE = 'USER';

export function isUserReport(item: Pick<ModerationReportItem, 'target_type'>): boolean {
  return item.target_type.toUpperCase() === USER_REPORT_TARGET_TYPE;
}

/**
 * DM-7976: target_type of a report or flag against a charity's organization
 * profile. It goes through the ordinary item action (approve/reject), but the
 * drawer shows the live profile beside the draft instead of an excerpt.
 */
export const ORGANIZATION_TARGET_TYPE = 'ORGANIZATION';

export function isOrganizationTarget(
  item: Pick<ModerationReportItem | FlaggedContentItem, 'target_type'>
): boolean {
  return item.target_type.toUpperCase() === ORGANIZATION_TARGET_TYPE;
}

export interface OrganizationProfilePayload {
  readonly live: ProfileFieldsWire;
  readonly draft: ProfileFieldsWire | null;
}

/**
 * The profile blocks an ORGANIZATION item carries, or null when the backend
 * sent none (an older queue payload) — the drawer then says so rather than
 * rendering an empty diff.
 */
export function organizationProfileOf(
  item: Pick<ModerationReportItem | FlaggedContentItem, 'profile_live' | 'profile_draft'>
): OrganizationProfilePayload | null {
  if (item.profile_live === undefined) return null;
  return { live: item.profile_live, draft: item.profile_draft ?? null };
}

/** The item actions a post, comment or other content target admits. */
export const MODERATION_ACTIONS: ReasonOption[] = [
  { value: 'APPROVE', label: 'Approve — content is acceptable' },
  { value: 'FLAG', label: 'Flag — restrict visibility' },
  { value: 'REMOVE', label: 'Remove — take content down' },
  { value: 'WARN', label: 'Warn — keep content, warn the author' },
  { value: 'DISMISS', label: 'Dismiss — report is unfounded' },
];

/**
 * DM-7976: an ORGANIZATION target admits APPROVE and REMOVE only (the
 * moderation-review validator answers 400 to anything else): APPROVE
 * publishes the flagged profile draft, REMOVE refuses it with the note as
 * the reason. No violation category is recorded for a profile.
 */
export const ORGANIZATION_MODERATION_ACTIONS: ReasonOption[] = [
  { value: 'APPROVE', label: 'Approve — publish the profile change' },
  { value: 'REMOVE', label: 'Remove — refuse the profile change' },
];

/**
 * DM-7976 (B1): target_type of a report filed for an escalated charity logo
 * or cover (target_id = the image id). Like a profile it admits APPROVE and
 * REMOVE only: APPROVE publishes the held image, REMOVE refuses it.
 */
export const ORGANIZATION_IMAGE_TARGET_TYPE = 'ORGANIZATION_IMAGE';

export function isOrganizationImageTarget(
  item: Pick<ModerationReportItem | FlaggedContentItem, 'target_type'>
): boolean {
  return item.target_type.toUpperCase() === ORGANIZATION_IMAGE_TARGET_TYPE;
}

export const ORGANIZATION_IMAGE_MODERATION_ACTIONS: ReasonOption[] = [
  { value: 'APPROVE', label: 'Approve — publish the image' },
  { value: 'REMOVE', label: 'Remove — refuse the image' },
];

/** The image block an ORGANIZATION_IMAGE report carries, or null when absent. */
export function organizationImageOf(
  item: Pick<ModerationReportItem, 'organization_image'>
): OrganizationImageModerationWire | null {
  return item.organization_image ?? null;
}

export const ORGANIZATION_IMAGE_MISSING_TEXT =
  'This item carries no image payload. Reload the queue: the held image’s link lasts 60 seconds.';

/** The actions offered for an item's target type. */
export function moderationActionsFor(
  item: Pick<ModerationReportItem | FlaggedContentItem, 'target_type'>
): ReasonOption[] {
  if (isOrganizationImageTarget(item)) return ORGANIZATION_IMAGE_MODERATION_ACTIONS;
  return isOrganizationTarget(item) ? ORGANIZATION_MODERATION_ACTIONS : MODERATION_ACTIONS;
}

/** Charity targets (profile or image) record no violation category. */
export function recordsViolationType(
  item: Pick<ModerationReportItem | FlaggedContentItem, 'target_type'>
): boolean {
  return !isOrganizationTarget(item) && !isOrganizationImageTarget(item);
}

export const ORGANIZATION_PROFILE_MISSING_TEXT =
  'This item carries no profile payload. Open the organization’s claim on the Charity Verification page to compare its profile.';

export const USER_REPORT_ACTIONS: ReasonOption[] = [
  { value: 'DISMISS', label: 'Dismiss — reports are unfounded' },
  { value: 'WARN', label: 'Warn — record a warning on the user' },
  { value: 'REMOVE', label: 'Remove user — terms violation, needs a second approver' },
];

/**
 * What each choice will do, shown under the select before anything is
 * submitted. REMOVE is the one that leaves the console: it parks a
 * TERMS_VIOLATION deletion for a second operator, after which the account is
 * suspended and its deletion scheduled without a donor-cancellable window.
 */
export function userReportActionHint(action: string): string {
  switch (action as UserReportResolutionAction) {
    case 'DISMISS':
      return 'Closes every open report against this user. Nothing happens to the account.';
    case 'WARN':
      return 'Closes the reports and records a warning on the user’s enforcement timeline.';
    case 'REMOVE':
      return 'Closes the reports and requests account deletion for a terms violation. A second operator must approve; on approval the account is suspended and the deletion scheduled.';
    default:
      return '';
  }
}

/** The drawer title for a report row; names the user case explicitly. */
export function reportDrawerTitle(item: Pick<ModerationReportItem, 'target_type'>): string {
  return isUserReport(item)
    ? 'Report — USER (resolve against the user)'
    : `Report — ${item.target_type}`;
}

/**
 * The toast after resolving reports against a user. Driven by what the
 * server did, not by what was asked: a REMOVE over a removal already in
 * effect closes the reports with no approval to wait for.
 */
export function userReportResolutionMessage(
  result: Pick<UserReportsResolved, 'closed_report_count' | 'pending_action_id'>,
  action: UserReportResolutionAction
): string {
  const closed = `Reports closed (${result.closed_report_count})`;
  if (result.pending_action_id) {
    return `${closed}; removal submitted for dual-control approval`;
  }
  if (action === 'REMOVE') {
    return `${closed}; the account's removal was already in effect`;
  }
  return `${closed} with ${action}`;
}
